import { Card, CardContent } from "@material-ui/core";
import AccountPermissionGroup from "@saleor/components/AccountPermissionGroup";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import { ControlledCheckbox } from "@saleor/components/ControlledCheckbox";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { StaffErrorFragment } from "@saleor/fragments/types/StaffErrorFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { getUserName } from "@saleor/misc";
import { SearchBrands_search_edges_node } from "@saleor/searches/types/SearchBrands";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { SearchPermissionGroups_search_edges_node } from "@saleor/searches/types/SearchPermissionGroups";
import { SearchTenantsByBrandId_search_edges_node } from "@saleor/searches/types/SearchTenantsByBrandId";
import { MallIdSearch_user_malls_edges_node } from "@saleor/staff/types/MallIdSearch";
import { FetchMoreProps, SearchPageProps } from "@saleor/types";
import { LockStatus, StaffPromoCreatorEnum } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";

import { StaffMemberDetails_user } from "../../types/StaffMemberDetails";
import { getBrandChoices, getMallChoices, getTenantChoices } from "../../utils";
import StaffAssignmentDropdowns from "../StaffAssignmentDropdowns";
import StaffMallList from "../StaffMallList";
import StaffProperties from "../StaffProperties/StaffProperties";
import { hasNoError, useAccountValidation } from "./Validations";

export interface StaffDetailsFormData {
  mallId: string;
  brandId: string;
  tenantId: string;
  contactNo: string;
  email: string;
  firstName: string;
  isActive: boolean;
  lastName: string;
  mallBranch: string;
  malls: string[];
  promoCreator: StaffPromoCreatorEnum | null;
  permissionGroups: string[];
  permissionGroup: string;
}

export interface StaffDetailsPageProps extends SearchPageProps {
  staffMemberUserType: any;
  assignedMallName: string;
  assignedTenantName: string;
  assignedBrandName: string;
  assignedCompanyNameByBrandId: string;
  showBrands: boolean;
  showMalls: boolean;
  showTenants: boolean;

  mallChoices: SearchMalls_search_edges_node[];
  brandChoices: SearchBrands_search_edges_node[];
  tenantChoices: SearchTenantsByBrandId_search_edges_node[];

  setEndCursor: (endCursor: string) => void;
  fetchMalls: (query: string) => void;
  fetchBrands: (query: string) => void;
  fetchTenants: (query: string) => void;

  handleUserTypeChange: (selectedValue) => void;
  availablePermissionGroups: SearchPermissionGroups_search_edges_node[];
  canEditAvatar: boolean;
  // canEditPermission: boolean;
  // canEditStatus: boolean;
  canChangePassword: boolean;
  canChangeOtherStaffPassword: boolean;
  canRemove: boolean;
  disabled: boolean;
  isUserSameAsViewer: boolean;
  fetchTenantsByBrandId: (id) => void;
  staffMemberMalls: MallIdSearch_user_malls_edges_node[];
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreBrands?: FetchMoreProps;
  fetchMoreTenants?: FetchMoreProps;
  fetchMorePermissionGroups: FetchMoreProps;
  saveButtonBarState: ConfirmButtonTransitionState;
  staffMember: StaffMemberDetails_user;
  errors: StaffErrorFragment[];
  onBack: () => void;
  onChangePassword: () => void;
  onChangeOtherPw: () => void;
  onDelete: () => void;
  onImageDelete: () => void;
  onSubmit: (data: StaffDetailsFormData) => SubmitPromise;
  onImageUpload(file: File);
}

const StaffDetailsPage: React.FC<StaffDetailsPageProps> = ({
  staffMemberUserType,
  assignedMallName,
  assignedTenantName,
  assignedBrandName,
  assignedCompanyNameByBrandId,
  mallChoices: mallList,
  brandChoices: brandList,
  tenantChoices: tenantList,
  fetchMalls,
  fetchBrands,
  fetchTenants,

  fetchMoreMalls,
  fetchMoreBrands,
  fetchMoreTenants,

  handleUserTypeChange,
  fetchTenantsByBrandId,

  showBrands,
  showMalls,
  showTenants,

  availablePermissionGroups,
  canEditAvatar,

  canChangePassword,
  canChangeOtherStaffPassword,
  canRemove,
  disabled,
  errors,
  fetchMorePermissionGroups,
  initialSearch,
  isUserSameAsViewer,
  onBack,
  onChangePassword,
  onChangeOtherPw,
  onDelete,
  onImageDelete,
  onImageUpload,
  onSearchChange,
  onSubmit,
  saveButtonBarState,
  staffMember,
  staffMemberMalls
}: StaffDetailsPageProps) => {
  const intl = useIntl();

  const initialForm: StaffDetailsFormData = {
    brandId: "",
    contactNo: staffMember?.contactNo || "",
    email: staffMember?.email || "",
    firstName: staffMember?.firstName || "",
    isActive: staffMember?.status === LockStatus.ACTIVE,
    lastName: staffMember?.lastName || "",
    mallBranch: "",
    mallId: "",
    malls: staffMemberMalls?.map(edge => edge.id) || [],
    permissionGroup: "",
    permissionGroups: staffMember?.permissionGroups.map(pg => pg.id) || [],
    promoCreator: staffMember?.promoCreator || null,
    tenantId: ""
  };

  const [mallDisplayValue, setMallDisplayValue] = useStateFromProps("");
  const [brandDisplayValue, setBrandDisplayValue] = useStateFromProps("");
  const [tenantDisplayValue, setTenantDisplayValue] = useStateFromProps("");
  const [
    selectedPermissionGroup,
    setSelectedPermissionGroup
  ] = useStateFromProps("");

  const mallChoices = getMallChoices(mallList);
  const brandChoices = getBrandChoices(brandList);
  const tenantChoices = getTenantChoices(tenantList);

  const [validationError, setValidationError] = React.useState({
    contactNo: null,
    firstName: null,
    lastName: null
  });

  const handleSubmit = event => {
    setValidationError({
      contactNo: null,
      firstName: null,
      lastName: null
    });
    const validate = useAccountValidation(event);
    if (hasNoError(validate)) {
      onSubmit(event);
    } else {
      setValidationError(validate);
    }
  };

  useEffect(() => {
    if (staffMember?.permissionGroups.length > 0) {
      setSelectedPermissionGroup(staffMember?.permissionGroups[0].name);
    }
  }, [staffMember?.permissionGroups]);

  const hasManageCouponsPermission = React.useMemo(
    () =>
      staffMember?.userPermissions.some(
        permission => permission.code === "MANAGE_COUPONS"
      ),
    [staffMember]
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submitWithOpts, hasChanged }) => {
        const availablePermissionGroupsChoices =
          availablePermissionGroups?.map(group => ({
            label: group.name,
            value: group.id
          })) || [];

        const permissionGroupChange = createSingleAutocompleteSelectHandler(
          change,
          selectedValue => {
            setSelectedPermissionGroup(selectedValue);
            handleUserTypeChange(selectedValue);
            // We always clear assignment fields after changing User Type dropdown selection
            setMallDisplayValue("");
            setBrandDisplayValue("");
            setTenantDisplayValue("");
            change({ target: { name: "brandId", value: "" } });
            change({ target: { name: "mallId", value: "" } });
            change({ target: { name: "tenantId", value: "" } });
          },
          availablePermissionGroupsChoices
        );
        // Prevent maximum update depth exceeded Error.
        const mallChange = createSingleAutocompleteSelectHandler(
          change,
          setMallDisplayValue,
          mallChoices
        );
        const brandChange = createSingleAutocompleteSelectHandler(
          change,
          setBrandDisplayValue,
          brandChoices
        );
        const tenantChange = createSingleAutocompleteSelectHandler(
          change,
          setTenantDisplayValue,
          tenantChoices
        );

        const handleChange = event => {
          const { name, value } = event.target;
          // change(event);
          if (name === "mallId") {
            setMallDisplayValue(value);
          } else if (name === "brandId") {
            setBrandDisplayValue(value);
            fetchTenantsByBrandId(value);
          } else if (name === "tenantId") {
            setTenantDisplayValue(value);
          }
        };

        const breadcrumbDirectory = [
          {
            name: "Users",
            url: "/staff"
          },
          {
            name: getUserName(staffMember)
          }
        ];

        const handleMallChange = newMallIds => {
          change({
            target: {
              name: "malls",
              value: newMallIds
            }
          });
        };

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader title={getUserName(staffMember)} />
            </div>
            <Grid>
              <div>
                <StaffProperties
                  canChangePassword={canChangePassword}
                  canChangeOtherStaffPassword={canChangeOtherStaffPassword}
                  staffMemberUserType={staffMemberUserType}
                  assignedMallName={assignedMallName}
                  assignedTenantName={assignedTenantName}
                  assignedBrandName={assignedBrandName}
                  assignedCompanyNameByBrandId={assignedCompanyNameByBrandId}
                  errors={errors}
                  data={formData}
                  disabled={disabled}
                  canEditAvatar={canEditAvatar}
                  isUserSameAsViewer={isUserSameAsViewer}
                  localError={validationError}
                  staffMember={staffMember}
                  selectedPermissionGroup={selectedPermissionGroup}
                  onChange={change}
                  onImageUpload={onImageUpload}
                  onImageDelete={onImageDelete}
                  onChangePassword={onChangePassword}
                  onChangeOtherPw={onChangeOtherPw}
                />
                <CardSpacer />
                {hasManageCouponsPermission && (
                  <StaffMallList
                    disabled={false}
                    emptyText="Assigned to all malls"
                    title="Malls"
                    malls={staffMemberMalls}
                    fetchMoreMalls={fetchMoreMalls}
                    onUpdateSelectedMalls={handleMallChange}
                    formData={formData}
                  />
                )}
              </div>
              <div>
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Permissions",
                      description: "dialog header"
                    })}
                  />
                  <CardContent>
                    <AccountPermissionGroup
                      formData={formData}
                      disabled={disabled}
                      errors={errors}
                      initialSearch={initialSearch}
                      availablePermissionGroups={availablePermissionGroups}
                      onChange={permissionGroupChange}
                      onSearchChange={onSearchChange}
                      displayValue={selectedPermissionGroup}
                      {...fetchMorePermissionGroups}
                    />
                    <StaffAssignmentDropdowns
                      errors={errors}
                      mallChoices={mallChoices}
                      disabled={disabled}
                      brandChoices={brandChoices}
                      tenantChoices={tenantChoices}
                      mallDisplayValue={mallDisplayValue}
                      brandDisplayValue={brandDisplayValue}
                      tenantDisplayValue={tenantDisplayValue}
                      formData={formData}
                      showBrands={showBrands}
                      showMalls={showMalls}
                      showTenants={showTenants}
                      handleChange={handleChange}
                      mallChange={mallChange}
                      brandChange={brandChange}
                      tenantChange={tenantChange}
                      fetchMalls={query => fetchMalls(query)}
                      fetchBrands={query => fetchBrands(query)}
                      fetchTenants={query => fetchTenants(query)}
                      fetchMoreMalls={fetchMoreMalls}
                      fetchMoreBrands={fetchMoreBrands}
                      fetchMoreTenants={fetchMoreTenants}
                    />
                  </CardContent>
                </Card>
                <CardSpacer />
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Status",
                      description: "section header"
                    })}
                  />
                  <CardContent>
                    <ControlledCheckbox
                      id="activeCheckbox"
                      checked={formData.isActive}
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "User is Active",
                        description: "status label"
                      })}
                      name="isActive"
                      onChange={change}
                    />
                  </CardContent>
                </Card>
              </div>
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              state={saveButtonBarState}
              onCancel={onBack}
              onSave={() => submitWithOpts({ clearUserType: true })}
              onDelete={canRemove ? onDelete : undefined}
            />
          </Container>
        );
      }}
    </Form>
  );
};
StaffDetailsPage.displayName = "StaffDetailsPage";
export default StaffDetailsPage;
